import React, { useEffect, useState } from "react";
import { Card, Table, Typography, Button, Row, Col, Spin } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import LabDashboardFilter from "../LabDashboard/components/LabDashboardFilter";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { actions, reducer, sliceKey } from "./slice";
import { commodityDetailedSaga } from "./saga";
import { useDispatch, useSelector } from "react-redux";
import * as selectors from "./selectors";
import { FaClipboardList } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";

const { Title, Text } = Typography;

const badgeColors = {
  total: "black",
  assigned: "#E6F0FF",
  accepted: "#E6F0FF",
  testing: "#E6F0FF",
  reportUploaded: "#2979FF",
  completed: "#2E7D32",
  underDispute: "#FFA000",
  pendingRegistration: "#E6F0FF",
  rejected: "#D32F2F",
};

const renderBadge = (value, type) => {
  if (value === 0 && type !== "total") return 0;

  const backgroundColor = badgeColors[type] || "#E6F0FF";

  const whiteTextTypes = [
    "total",
    "reportUploaded",
    "completed",
    "underDispute",
    "rejected",
  ];
  const textColor = whiteTextTypes.includes(type) ? "white" : "#000";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 26,
        minHeight: 26,
        padding: "4px 4px",
        borderRadius: "50%",
        backgroundColor,
        color: textColor,
        fontSize: "14px",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  );
};

const columns = [
  {
    title: <Text strong>Commodity</Text>,
    dataIndex: "commodity",
    key: "commodity",
    render: (text) => <Text style={{ fontWeight: 600 }}>{text}</Text>,
  },
  {
    title: <Text strong>Total</Text>,
    dataIndex: "total",
    key: "total",
    align: "center",
    render: (val) => renderBadge(val, "total"),
  },
  {
    title: <Text strong>Pending Registration</Text>,
    dataIndex: "pendingRegistration",
    key: "pendingRegistration",
    align: "center",
    render: (val) => renderBadge(val, "pendingRegistration"),
  },

  {
    title: <Text strong>Accepted</Text>,
    dataIndex: "accepted",
    key: "accepted",
    align: "center",
    render: (val) => renderBadge(val, "accepted"),
  },
  {
    title: <Text strong>Testing</Text>,
    dataIndex: "testing",
    key: "testing",
    align: "center",
    render: (val) => renderBadge(val, "testing"),
  },
  {
    title: <Text strong>Report Uploaded</Text>,
    dataIndex: "reportUploaded",
    key: "reportUploaded",
    align: "center",
    render: (val) => renderBadge(val, "reportUploaded"),
  },
  {
    title: <Text strong>Completed</Text>,
    dataIndex: "completed",
    key: "completed",
    align: "center",
    render: (val) => renderBadge(val, "completed"),
  },
  {
    title: <Text strong>Under Dispute</Text>,
    dataIndex: "underDispute",
    key: "underDispute",
    align: "center",
    render: (val) => renderBadge(val, "underDispute"),
  },

  {
    title: <Text strong>Rejected</Text>,
    dataIndex: "rejected",
    key: "rejected",
    align: "center",
    render: (val) => renderBadge(val, "rejected"),
  },
];

const CommodityOrdersStatusOverview = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: commodityDetailedSaga });

  const location = useLocation();
  const {
    selectedCommoditiesFilter,
    dateRangeFilter,
    selectedDatePeriodFilter,
  } = location.state || {};

  const dispatch = useDispatch();
  const history = useHistory();

  const commodities = useSelector(selectors.selectCommodities);
  const commodityWiseDetails = useSelector(
    selectors.selectCommodityWiseDetails
  );

  const [selectedDatePeriod, setSelectedDatePeriod] = useState(
    selectedDatePeriodFilter || "all_time"
  );
  const [selectedCommodities, setSelectedCommodities] = useState(
    selectedCommoditiesFilter || []
  );
  const [dateRange, setDateRange] = useState(
    dateRangeFilter || {
      startDate: null,
      endDate: null,
    }
  );
  const [isLoading, setIsLoading] = useState(true);

  const data = commodityWiseDetails?.map((item, index) => ({
    key: index + 1,
    commodity: item.commodity || 0,
    total: item.total || 0,
    assigned: item.assigned || 0,
    accepted: item.accepted || 0,
    testing: item.underTesting || 0,
    reportUploaded: item.reportUploaded || 0,
    completed: item.completed || 0,
    underDispute: item.underDispute || 0,
    pendingRegistration: item.pendingRegistration || 0,
    rejected: item.rejected || 0,
  }));

  useEffect(() => {
    dispatch(actions.fetchCommodities());
  }, [dispatch]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      actions.getCommodityWiseDetailedOrders({
        selectedCommodities,
        dateRange,
        onSuccess: () => {
          setIsLoading(false);
        },
        onFailure: () => {
          setIsLoading(false);
        },
      })
    );
  }, [dispatch, selectedCommodities, dateRange]);

  useEffect(() => {
    // Clear location state when filters change manually
    if (location.state) {
      history.replace(location.pathname);
    }
  }, [
    selectedDatePeriod,
    selectedCommodities,
    dateRange,
    location.state,
    location.pathname,
    history,
  ]);

  const handleBackToDashboard = () => {
    history.push("/labPortal/dashboard", {
      selectedCommoditiesFilter: selectedCommodities,
      dateRangeFilter: dateRange,
      selectedDatePeriodFilter: selectedDatePeriod,
    });
  };

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Row align="middle" gutter={12}>
            <Col>
              <Button
                onClick={handleBackToDashboard}
                style={{
                  borderRadius: 8,
                  backgroundColor: "#08142c",
                  color: "#fff",
                  fontWeight: 600,
                  border: "1px solid #08142c",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <IoArrowBack size={16} />
                Back
              </Button>
            </Col>
            <Col>
              <Title
                level={4}
                style={{ margin: 0, display: "flex", alignItems: "center" }}
              >
                Commodity wise Orders – Detailed View
              </Title>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Filters Section */}
      <div style={{ marginBottom: 16 }}>
        <LabDashboardFilter
          commodities={commodities}
          selectedDatePeriod={selectedDatePeriod}
          setSelectedDatePeriod={setSelectedDatePeriod}
          selectedCommodities={selectedCommodities}
          setSelectedCommodities={setSelectedCommodities}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      </div>

      <Card style={{ borderRadius: 8 }}>
        <Title level={4} style={{ marginBottom: 4 }}>
          Commodity Orders Status Overview
        </Title>
        <Text type="secondary">
          Detailed breakdown of orders by commodity and their current status
        </Text>

        {isLoading ? (
          <div
            style={{
              minHeight: "42vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <Spin size="large" />
          </div>
        ) : commodityWiseDetails?.length === 0 ? (
          <DataNotFound />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            pagination={false}
            style={{ marginTop: 16 }}
            bordered
          />
        )}
      </Card>
    </>
  );
};

export default CommodityOrdersStatusOverview;

const DataNotFound = () => (
  <div
    style={{
      minHeight: "42vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <div
      style={{
        fontSize: "48px",
        marginBottom: "16px",
        color: "#d9d9d9",
      }}
    >
      <FaClipboardList color="#1C8A7A" />
    </div>
    <Title level={4} style={{ color: "#8c8c8c", marginBottom: 8 }}>
      No Commodity Wise Orders Found
    </Title>
    <Text type="secondary" style={{ fontSize: "14px" }}>
      No commodity wise orders found in the selected period.
    </Text>
  </div>
);
